import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import {
	Chip,
	IconButton,
	makeStyles,
	MenuItem,
	Paper,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@material-ui/core";
import { Close, OpenInNew, Replay } from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import ConfirmationModal from "../../components/ConfirmationModal";
import ResponsiveTable from "../../components/ResponsiveTable";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const REFRESH_MS = 15000;
const PAGE_SIZE = 25;
const STATUSES = ["pending", "sent", "failed", "canceled"];
const STATUS_COLOR = {
	pending: "primary",
	sent: "default",
	failed: "secondary",
	canceled: "default",
};

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	hint: {
		margin: theme.spacing(0, 0, 1.5),
		color: theme.palette.text.secondary,
	},
	filters: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1.5),
	},
	field: {
		flex: "1 1 160px",
		minWidth: 140,
	},
	search: {
		flex: "2 1 220px",
	},
	body: {
		maxWidth: 320,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		[theme.breakpoints.down("xs")]: {
			whiteSpace: "normal",
			maxWidth: "none",
		},
	},
	muted: {
		display: "block",
		color: theme.palette.text.secondary,
	},
	empty: {
		padding: theme.spacing(4),
		textAlign: "center",
		color: theme.palette.text.secondary,
	},
}));

// "2026-10-17" -> start / end of that day in the browser's time zone
const dayStart = value => (value ? new Date(`${value}T00:00:00`).toISOString() : undefined);
const dayEnd = value => (value ? new Date(`${value}T23:59:59.999`).toISOString() : undefined);

const Schedules = () => {
	const classes = useStyles();
	const history = useHistory();
	const { user } = useContext(AuthContext);
	const { whatsApps } = useContext(WhatsAppsContext);
	const isAdmin = user.profile === "admin";

	const [status, setStatus] = useState("");
	const [whatsappId, setWhatsappId] = useState("");
	const [userId, setUserId] = useState("");
	const [from, setFrom] = useState("");
	const [to, setTo] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [data, setData] = useState({ items: [], total: 0 });
	const [loaded, setLoaded] = useState(false);
	const [users, setUsers] = useState([]);
	const [confirm, setConfirm] = useState(null); // item to cancel

	useEffect(() => {
		const timer = setTimeout(() => {
			setSearch(searchInput.trim());
			setPage(0);
		}, 400);
		return () => clearTimeout(timer);
	}, [searchInput]);

	useEffect(() => {
		if (!isAdmin) return;
		api
			.get("/users/", { params: { pageNumber: 1 } })
			.then(({ data: result }) => setUsers(result.users || []))
			.catch(toastError);
	}, [isAdmin]);

	const load = useCallback(async () => {
		try {
			const { data: result } = await api.get("/scheduled", {
				params: {
					status: status || undefined,
					whatsappId: whatsappId || undefined,
					userId: userId || undefined,
					from: dayStart(from),
					to: dayEnd(to),
					search: search || undefined,
					page: page + 1,
					pageSize: PAGE_SIZE,
				},
			});
			setData(result);
		} catch (err) {
			toastError(err);
		} finally {
			setLoaded(true);
		}
	}, [status, whatsappId, userId, from, to, search, page]);

	useEffect(() => {
		load();
		const timer = setInterval(load, REFRESH_MS);
		return () => clearInterval(timer);
	}, [load]);

	const onFilter = setter => e => {
		setter(e.target.value);
		setPage(0);
	};

	const cancel = async () => {
		const item = confirm;
		setConfirm(null);
		try {
			await api.delete(`/scheduled/${item.id}`);
			toast.success(i18n.t("schedules.canceled"));
			await load();
		} catch (err) {
			toastError(err);
		}
	};

	const retry = async item => {
		try {
			await api.post(`/scheduled/${item.id}/retry`);
			toast.success(i18n.t("schedules.retried"));
			await load();
		} catch (err) {
			toastError(err);
		}
	};

	const recurrenceLabel = item =>
		item.recurrence && item.recurrence !== "none"
			? i18n.t(`schedule.recurrence.${item.recurrence}`)
			: i18n.t("schedule.recurrence.none");

	return (
		<MainContainer>
			<ConfirmationModal
				title={i18n.t("schedules.confirmCancelTitle")}
				open={!!confirm}
				onClose={() => setConfirm(null)}
				onConfirm={cancel}
			>
				{confirm && `"${confirm.body.slice(0, 120)}"`}
			</ConfirmationModal>

			<MainHeader>
				<Title>{i18n.t("schedules.title")}</Title>
			</MainHeader>

			<Paper className={classes.mainPaper} variant="outlined">
				<Typography variant="body2" className={classes.hint}>
					{isAdmin ? i18n.t("schedules.hintAdmin") : i18n.t("schedules.hintOwn")}
				</Typography>

				<div className={classes.filters}>
					<TextField
						className={classes.search}
						size="small"
						variant="outlined"
						label={i18n.t("schedules.search")}
						value={searchInput}
						onChange={e => setSearchInput(e.target.value)}
					/>
					<TextField
						select
						className={classes.field}
						size="small"
						variant="outlined"
						label={i18n.t("schedules.filters.status")}
						value={status}
						onChange={onFilter(setStatus)}
					>
						<MenuItem value="">{i18n.t("schedules.filters.all")}</MenuItem>
						{STATUSES.map(s => (
							<MenuItem key={s} value={s}>
								{i18n.t(`schedule.status.${s}`)}
							</MenuItem>
						))}
					</TextField>
					<TextField
						select
						className={classes.field}
						size="small"
						variant="outlined"
						label={i18n.t("schedules.filters.connection")}
						value={whatsappId}
						onChange={onFilter(setWhatsappId)}
					>
						<MenuItem value="">{i18n.t("schedules.filters.allConnections")}</MenuItem>
						{whatsApps.map(w => (
							<MenuItem key={w.id} value={w.id}>
								{w.name}
							</MenuItem>
						))}
					</TextField>
					{isAdmin && (
						<TextField
							select
							className={classes.field}
							size="small"
							variant="outlined"
							label={i18n.t("schedules.filters.createdBy")}
							value={userId}
							onChange={onFilter(setUserId)}
						>
							<MenuItem value="">{i18n.t("schedules.filters.everyone")}</MenuItem>
							{users.map(u => (
								<MenuItem key={u.id} value={u.id}>
									{u.name}
								</MenuItem>
							))}
						</TextField>
					)}
					<TextField
						type="date"
						className={classes.field}
						size="small"
						variant="outlined"
						label={i18n.t("schedules.filters.from")}
						value={from}
						onChange={onFilter(setFrom)}
						InputLabelProps={{ shrink: true }}
					/>
					<TextField
						type="date"
						className={classes.field}
						size="small"
						variant="outlined"
						label={i18n.t("schedules.filters.to")}
						value={to}
						onChange={onFilter(setTo)}
						InputLabelProps={{ shrink: true }}
					/>
				</div>

				{loaded && data.items.length === 0 ? (
					<div className={classes.empty}>
						<Typography variant="body1">{i18n.t("schedules.empty")}</Typography>
					</div>
				) : (
					<ResponsiveTable>
						<TableHead>
							<TableRow>
								<TableCell>{i18n.t("schedules.table.contact")}</TableCell>
								<TableCell>{i18n.t("schedules.table.message")}</TableCell>
								<TableCell>{i18n.t("schedules.table.sendAt")}</TableCell>
								<TableCell align="center">{i18n.t("schedules.table.status")}</TableCell>
								<TableCell>{i18n.t("schedules.table.connection")}</TableCell>
								{isAdmin && <TableCell>{i18n.t("schedules.table.createdBy")}</TableCell>}
								<TableCell align="center">{i18n.t("schedules.table.actions")}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{data.items.map(item => (
								<TableRow key={item.id}>
									<TableCell>
										{item.contactName || "—"}
										<span className={classes.muted}>{item.contactNumber}</span>
									</TableCell>
									<TableCell data-label={i18n.t("schedules.table.message")}>
										<Tooltip title={item.body}>
											<span className={classes.body}>{item.body}</span>
										</Tooltip>
									</TableCell>
									<TableCell data-label={i18n.t("schedules.table.sendAt")}>
										{new Date(item.sendAt).toLocaleString()}
										<span className={classes.muted}>{recurrenceLabel(item)}</span>
									</TableCell>
									<TableCell align="center" data-label={i18n.t("schedules.table.status")}>
										<Tooltip title={item.lastError || ""}>
											<Chip
												size="small"
												color={STATUS_COLOR[item.status]}
												variant={item.status === "pending" ? "default" : "outlined"}
												label={i18n.t(`schedule.status.${item.status}`)}
											/>
										</Tooltip>
									</TableCell>
									<TableCell data-label={i18n.t("schedules.table.connection")}>
										{item.whatsappName || "—"}
									</TableCell>
									{isAdmin && (
										<TableCell data-label={i18n.t("schedules.table.createdBy")}>
											{item.userName || "—"}
										</TableCell>
									)}
									<TableCell align="center">
										<Tooltip title={i18n.t("schedules.openTicket")}>
											<IconButton
												size="small"
												onClick={() => history.push(`/tickets/${item.ticketId}`)}
											>
												<OpenInNew fontSize="small" />
											</IconButton>
										</Tooltip>
										{item.status === "failed" && (
											<Tooltip title={i18n.t("schedules.retry")}>
												<IconButton size="small" onClick={() => retry(item)}>
													<Replay fontSize="small" />
												</IconButton>
											</Tooltip>
										)}
										{item.status === "pending" && (
											<Tooltip title={i18n.t("schedules.cancel")}>
												<IconButton size="small" onClick={() => setConfirm(item)}>
													<Close fontSize="small" />
												</IconButton>
											</Tooltip>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</ResponsiveTable>
				)}

				{data.total > PAGE_SIZE && (
					<TablePagination
						component="div"
						count={data.total}
						page={page}
						rowsPerPage={PAGE_SIZE}
						rowsPerPageOptions={[PAGE_SIZE]}
						onChangePage={(_, next) => setPage(next)}
						labelDisplayedRows={({ from: a, to: b, count }) => `${a}–${b} / ${count}`}
					/>
				)}
			</Paper>
		</MainContainer>
	);
};

export default Schedules;
